import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Modal, Spinner } from '../../components/ui';
import { fullName, fmtDate, fmtTime, isPastFunction, nameInput, tenDigits, MAX_NAME, GUEST_TYPE_OPTIONS } from '../../utils/helpers';
import ExportButton from '../../components/ExportButton';
import MemberForm from './MemberForm';
import QuickAdd from './QuickAdd';

// One line under an open family: name, optional number, Add. It saves through
// the same call the Families screen uses (an existing guest with that number is
// MOVED into the family, never duplicated) and stays focused for the next one.
function FamilyAddRow({ head, span, onAdd }) {
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const add = async () => {
    const guestName = name.trim();
    if (!guestName || busy) return;
    setBusy(true);
    try {
      await onAdd({ GuestName: guestName, Surname: head.Surname || null, MobileNumber: mobile || null });
      setName(''); setMobile('');
      ref.current?.focus();
    } finally { setBusy(false); }
  };

  return (
    <tr className="fam-add-row">
      <td className="g-name">
        <span className="fam-indent">└</span>
        <input ref={ref} value={name} maxLength={MAX_NAME} placeholder="Add member name"
          onChange={e => setName(nameInput(e.target.value))}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
      </td>
      <td className="g-mobile">
        <input value={mobile} placeholder="optional" inputMode="numeric" maxLength={10}
          onChange={e => setMobile(tenDigits(e.target.value))}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
      </td>
      <td colSpan={span} className="fam-add-note muted">
        takes {head.Surname || 'the head\u2019s'} surname and this family\u2019s functions
      </td>
      <td className="guest-row-actions">
        <button className="btn primary sm" disabled={!name.trim() || busy} onClick={add}>
          {busy ? '…' : '+ Add'}
        </button>
      </td>
    </tr>
  );
}

// Desktop UI for Members — wide table.
export default function MembersDesktop({ vm }) {
  const {
    isAdmin, canAdd, canEdit, canDelete, mine, functions, invitableFunctions, loading, filtered, stats,
    q, setQ, fnFilter, setFnFilter, fnCount, fnMode, setFnMode, fnFilterLabel, fnName, openAdd, openEdit, del, open,
    inviteRow, inviteSharing, inviteCooldown, openInvite, closeInvite, sendInvite, guestHasAllFunctions,
    isMarriage, sort, setSort, toggleSort, SORT_OPTIONS, quick,
    toggleRowFunction, rowHasFunction, savingFn, cardInfo, sideFilter, setSideFilter, sideCounts,
    cardFilter, setCardFilter, cardCounts, openFamilyFor, report,
    adderOptions, adderFilter, setAdderFilter, adder, listView, setListView, familyGroups, individualRows, businessRows, viewCounts,
    addOrMoveFamilyMember, isPublic, setVisibilityFor, savingVis, setGuestTypeFor, visFilter, setVisFilter, visCounts, typeCounts, guestTypeFilter, setGuestTypeFilter,
  } = vm;

  // Families view: which families are unfolded.
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleFamily = (key) => setExpanded(s => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const familiesWithMembers = familyGroups.filter(g => g.members.length > 0);
  const allOpen = familiesWithMembers.length > 0 && familiesWithMembers.every(g => expanded.has(g.key));
  const toggleAll = () => setExpanded(allOpen ? new Set() : new Set(familiesWithMembers.map(g => g.key)));

  // Every guest the current view lists (a folded family's members included) -
  // what the Public header box marks.
  const shownRows = listView === 'families' ? familyGroups.flatMap(g => [g.head, ...g.members])
    : listView === 'individuals' ? individualRows
      : listView === 'business' ? businessRows : filtered;
  const editableShown = shownRows.filter(r => mine(r));
  const publicShown = shownRows.filter(r => isPublic(r)).length;

  // Right-click menu on a guest row: { x, y, row } or null.
  const [menu, setMenu] = useState(null);
  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); } };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey, true);
    return () => { window.removeEventListener('click', close); window.removeEventListener('scroll', close, true); window.removeEventListener('keydown', onKey, true); };
  }, [menu]);
  const openMenu = (e, row) => {
    e.preventDefault();
    setMenu({ x: Math.min(e.clientX, window.innerWidth - 260), y: Math.min(e.clientY, window.innerHeight - 190), row });
  };

  // Columns: only functions still to come — one that is over (e.g. the June
  // Cocktail Party) is not shown; its invitations stay saved.
  const tableFunctions = functions.filter(f => !isPastFunction(f));

  // The table header sticks right under the toolbar, whose height changes as
  // it wraps - so measure it into --guest-bar-h (used by .guests-page-desktop CSS).
  const pageRef = useRef(null);
  const barRef = useRef(null);
  useLayoutEffect(() => {
    const bar = barRef.current, page = pageRef.current;
    if (!bar || !page || typeof ResizeObserver === 'undefined') return undefined;
    const set = () => page.style.setProperty('--guest-bar-h', `${bar.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(bar);
    return () => ro.disconnect();
  }, []);

  // Column header that sorts on click; shows ▲/▼ on the active column.
  const Th = ({ k, children, className, title, rowSpan }) => (
    <th className={(className || '') + ' sortable' + (sort.key === k ? ' active' : '')} title={title || 'Sort'}
      rowSpan={rowSpan} onClick={() => toggleSort(k)}>
      {children}{sort.key === k && <span className="arrow">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );

  // One guest row (both views). fam: { lead, count, open, key } on a family's
  // first row in the Families view, { nested } on its members.
  const renderRow = (row, fam = null) => {
                const editable = mine(row);
    // Second line under the name: whatever detail exists, joined compactly.
    const detail = [row.Relation, row.GroupName, row.City].filter(Boolean).join(' · ');
    const card = cardInfo(row);
    return (
      <tr key={row.GuestId} onContextMenu={(e) => openMenu(e, row)}
        onClick={fam && fam.lead && fam.count > 0 ? (e) => {
          if (e.target.closest('button, input, select, a')) return;
          toggleFamily(fam.key);
        } : undefined}
        title={fam && fam.lead && fam.count > 0 ? (fam.open ? 'Click to fold this family' : 'Click to see the family members') : undefined}
        className={[menu && menu.row.GuestId === row.GuestId ? 'ctx-on' : '', fam && fam.nested ? 'fam-member-row' : '', fam && fam.lead ? 'fam-head-row' : '', fam && fam.lead && fam.count > 0 ? 'fam-clickable' : ''].filter(Boolean).join(' ') || undefined}>
        <td className="g-name">
          {fam && fam.lead && fam.count > 0 && (
            <button type="button" className="fam-caret" title={fam.open ? 'Fold the family' : 'Show the family members'}
              onClick={() => toggleFamily(fam.key)}>{fam.open ? '▾' : '▸'} +{fam.count}</button>
          )}
          {fam && fam.nested && <span className="fam-indent">└</span>}
          <button type="button" className="g-name-btn" disabled={!(editable && canEdit)}
            title={editable && canEdit ? 'Open this guest' : undefined} onClick={() => openEdit(row)}>
            {fullName(row)}
          </button>
          {(row.Visibility || 'Private') === 'Private' && <span className="g-private" title="Private — only the adder and admins see this guest">🔒</span>}
          {(detail || row.Remarks || Number(row.AddedByGuest) === 1) && (
            <div className="sub" title={row.Remarks || undefined}>
              {detail}{detail && row.Remarks ? ' · ' : ''}{row.Remarks ? <em>{row.Remarks}</em> : null}
              {Number(row.AddedByGuest) === 1 && <span className="chip guest-added">Guest</span>}
            </div>
          )}
        </td>
        <td className="g-mobile">{row.MobileNumber || '—'}</td>
        {/* Firm above, city below - a family guest shows only the city. */}
        <td className="g-firm">
          {String(row.GuestType || 'Family') === 'Business' && row.CompanyName
            ? <><strong>{row.CompanyName}</strong>{row.City ? <div className="sub">{row.City}</div> : null}</>
            : <span className="muted">{row.City || '—'}</span>}
        </td>
        {tableFunctions.map(f => {
          const on = rowHasFunction(row, f.FunctionId);
          const past = isPastFunction(f);
          const busy = savingFn === `${row.GuestId}:${f.FunctionId}`;
          return (
            <td key={f.FunctionId} className={'fn-col' + (on ? ' on' : '')}>
              <input type="checkbox" checked={on} disabled={!(editable && canEdit) || past || !!savingFn}
                className={busy ? 'saving' : undefined}
                title={`${f.FunctionName}${past ? ' (over)' : ''}${on ? ' — invited' : ''}`}
                onChange={() => toggleRowFunction(row, f.FunctionId)} />
            </td>
          );
        })}
        <td className="g-card">
          <button type="button" className="g-card-btn" disabled={!(editable && canEdit)} onClick={() => openEdit(row)}
            title={`${card.kind === 'head' ? `Head of a family of ${card.size}` : card.kind === 'member' ? `On the card of ${card.headName}` : 'Own card'} — click to change it in the guest form`}>
            {card.kind === 'head' ? <span className="chip ok">Head{card.size > 1 ? ` · ${card.size}` : ''}</span>
              : card.kind === 'member' ? <span className="g-card-of">on {card.headName}</span>
                : <span className="muted">own</span>}
            {card.extra > 0 && <span className="chip warn" title="Extra cards">+{card.extra}</span>}
          </button>
        </td>
        <td className="fn-col vis-col">
          <input type="checkbox" checked={isPublic(row)} disabled={!(editable && canEdit) || savingVis}
            title={isPublic(row) ? 'Public — every member of the event sees this guest. Untick for Private.' : 'Private — only the adder and admins see this guest. Tick for Public.'}
            onChange={() => setVisibilityFor([row], !isPublic(row))} />
        </td>
        {/* Relative / Friend / Business: one box each, exactly one ticked -
            ticking another moves the guest there (saved at once). */}
        {GUEST_TYPE_OPTIONS.map(o => {
          const on = String(row.GuestType || 'Family') === o.value;
          return (
            <td key={o.value} className={'fn-col type-col type-' + o.value.toLowerCase() + (on ? ' on' : '')}>
              <input type="checkbox" checked={on} disabled={!(editable && canEdit) || on}
                title={on ? `${o.label}` : `Make this guest a ${o.label}`}
                onChange={() => setGuestTypeFor(row, o.value)} />
            </td>
          );
        })}
        <td className="guest-row-actions">
          <div className="guest-actions-inner">
            {/* WhatsApp invite: right-click the row → 💬 Invite on WhatsApp. */}
            {editable && canEdit && <button className="icon-btn" title="Edit" onClick={() => openEdit(row)}>✎</button>}
            {editable && canDelete && <button className="icon-btn danger" title="Delete" onClick={() => del(row)}>🗑</button>}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="guests-page guests-page-desktop" ref={pageRef}>
      <div className="guest-controls-sticky guest-toolbar" ref={barRef}>
        <div className="guest-toolbar-title"
          title={`${stats.parties} guests · ${stats.persons} persons expected${stats.entries !== stats.parties ? ` · ${stats.entries} function invites` : ''} · ${fnFilterLabel}${isAdmin ? ' — full list across all members' : ' — yours plus Common ones'}`}>
          <h1>Guests</h1>
          <span className="guest-toolbar-sub"><strong>{stats.parties}</strong> · {stats.persons} pax</span>
          {listView === 'families' && familiesWithMembers.length > 0 && (
            <button type="button" className="linklike fam-all" onClick={toggleAll}>{allOpen ? 'fold all' : 'unfold all'}</button>
          )}
        </div>
        <div className="guest-toolbar-filters">
          {/* Every filter is a compact dropdown (the counts are in the options). */}
          {isMarriage && (
            <select value={sideFilter} onChange={e => setSideFilter(e.target.value)} title="Side"
              className={sideFilter ? 'filter-on' : undefined}>
              <option value="">All sides ({sideCounts.all})</option>
              <option value="Groom">Groom ({sideCounts.Groom})</option>
              <option value="Bride">Bride ({sideCounts.Bride})</option>
              {/* Guests on neither side - normally none; listed so stray ones can be found and fixed. */}
              {sideCounts.Common > 0 && <option value="Common">Common / both ({sideCounts.Common})</option>}
            </select>
          )}
          <select value={listView} onChange={e => setListView(e.target.value)} title="List view: all guests, only families (members folded under the head), or only guests in no family"
            className={listView !== 'people' ? 'filter-on' : undefined}>
            <option value="people">All guests ({viewCounts.people})</option>
            <option value="families">👪 Families ({viewCounts.families})</option>
            <option value="individuals">👤 Individuals ({viewCounts.individuals})</option>
            <option value="business">🏢 Business associates ({viewCounts.business})</option>
          </select>
          <select value={cardFilter} onChange={e => setCardFilter(e.target.value)} title="Cards: all, one row per invitation card (own + heads), or only guests on their own card"
            className={cardFilter ? 'filter-on' : undefined}>
            <option value="">All cards ({cardCounts.all})</option>
            <option value="cards">Own + Head ({cardCounts.cards})</option>
            <option value="own">Own card ({cardCounts.own})</option>
          </select>
          <select value={visFilter} onChange={e => setVisFilter(e.target.value)}
            title="Public = every member sees the guest · Private = only the adder and admins"
            className={visFilter ? 'filter-on' : undefined}>
            <option value="">Public + Private ({visCounts.all})</option>
            <option value="public">Public ({visCounts.public})</option>
            <option value="private">🔒 Private ({visCounts.private})</option>
          </select>
          <select value={guestTypeFilter} onChange={e => setGuestTypeFilter(e.target.value)} title="Guest type"
            className={guestTypeFilter !== 'all' ? 'filter-on' : undefined}>
            <option value="all">All types ({typeCounts.all})</option>
            <option value="relative">Relative ({typeCounts.relative})</option>
            <option value="friend">Friend ({typeCounts.friend})</option>
            <option value="business">Business ({typeCounts.business})</option>
          </select>
          <select value={fnFilter} onChange={e => setFnFilter(e.target.value)} title={`Function filter: ${fnFilterLabel}`}
            className={fnFilter ? 'filter-on' : undefined}>
            <option value="">All functions</option>
            <option value="__all">✔ Invited to ALL functions</option>
            <option value="__some">✘ Missing some functions</option>
            <optgroup label="One function">
              {functions.map(f => <option key={f.FunctionId} value={f.FunctionId}>{f.FunctionName} ({fnCount(f.FunctionId)})</option>)}
            </optgroup>
          </select>
          {fnFilter && !fnFilter.startsWith('__') && (
            <select value={fnMode} onChange={e => setFnMode(e.target.value)} title={`Invited or not invited to ${fnName(fnFilter)}`}
              className={fnMode === 'out' ? 'filter-on' : undefined}>
              <option value="in">Invited</option>
              <option value="out">Not invited</option>
            </select>
          )}
          {/* Whose guests: "All" or one person's share of the invitations. */}
          {adderOptions.length > 1 && (
            <select value={adderFilter} onChange={e => setAdderFilter(e.target.value)} title="Filter by who added the guest">
              <option value="">Added by: everyone</option>
              {adderOptions.map(u => <option key={u.value} value={u.value}>{u.label} ({u.count})</option>)}
            </select>
          )}
          <select value={sort.key} onChange={e => setSort(s => ({ key: e.target.value, dir: s.dir }))} title="Sort by">
            {SORT_OPTIONS.map(([k, l]) => <option key={k} value={k}>⇅ {l}</option>)}
          </select>
          <button className="btn light" title={sort.dir === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
            onClick={() => setSort(s => ({ ...s, dir: s.dir === 'asc' ? 'desc' : 'asc' }))}>
            {sort.dir === 'asc' ? 'A→Z' : 'Z→A'}
          </button>
          <input type="search" placeholder="Search…" title="Search by name or number" value={q} onChange={e => setQ(e.target.value)} />
        </div>
        <div className="spacer" />
        {canAdd && (
          <div className="guest-toolbar-actions">
            <button className={'btn light' + (quick.quickOpen ? ' active' : '')} disabled={!invitableFunctions.length}
              title="Type many guests at once, one line each" onClick={quick.quickOpen ? quick.closeQuick : quick.openQuick}>
              ⚡ Quick
            </button>
            <ExportButton report={report} className="btn light" />
            <button className="btn primary" onClick={() => openAdd('individual')} disabled={!invitableFunctions.length} title="Add one guest">+ Guest</button>
            <button className="btn light" onClick={() => openAdd('family')} disabled={!invitableFunctions.length} title="Add a family (one card)">+ Family</button>
          </div>
        )}
      </div>

      {adder && (
        <div className="muted added-by-line">
          <strong>Added by:</strong> {adder.label}
          <button type="button" className="linklike" onClick={() => setAdderFilter('')}>show everyone</button>
        </div>
      )}

      {functions.length > 0 && !invitableFunctions.length && (
        <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
          All functions of this event are over — guests can no longer be added.
        </div>
      )}
      {!functions.length && (
        <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
          No functions yet — an Event Admin must add functions before guests can be invited.
        </div>
      )}

      {quick.quickOpen && <QuickAdd vm={vm} />}

      <div className="card table-wrap guests-table-wrap">
        {loading ? <Spinner /> : filtered.length === 0 ? (
          <div className="empty">No guests yet.</div>
        ) : (
          <table className="tbl compact guests-table">
            {/* Fixed widths for the text columns; the function columns share
                whatever width is left, so the table fills the screen. */}
            <colgroup>
              <col style={{ width: 220 }} />
              <col style={{ width: 100 }} />
              <col style={{ width: 120 }} />
              {tableFunctions.map(f => <col key={f.FunctionId} />)}
              <col style={{ width: 150 }} />
              <col style={{ width: 58 }} />
              {GUEST_TYPE_OPTIONS.map(o => <col key={o.value} style={{ width: 62 }} />)}
              <col style={{ width: 58 }} />
            </colgroup>
            <thead>
              <tr>
                <Th k="name" className="g-name" rowSpan={2}>Guest</Th>
                <Th k="mobile" rowSpan={2}>Mobile</Th>
                <Th k="city" rowSpan={2} title="Firm for a business associate, city underneath">Firm / City</Th>
                {tableFunctions.length > 0 && <th className="fn-group" colSpan={tableFunctions.length}>Functions</th>}
                <th rowSpan={2} title="Whose invitation card this guest is on">Card</th>
                {/* Ticks / clears Public for EVERY guest on screen - choose
                    "Added by: <name>" first to mark only that person's guests. */}
                <th rowSpan={2} className="fn-col vis-col"
                  title={`Public = every member sees the guest · Private = only the adder and admins.\nThis box marks all ${editableShown.length} guests on screen${adder ? ` (added by ${adder.label})` : ''} at once.`}>
                  <span className="fn-col-name">Public <span className="fn-count">({publicShown})</span></span>
                  <input type="checkbox" ref={el => { if (el) el.indeterminate = publicShown > 0 && publicShown < shownRows.length; }}
                    checked={shownRows.length > 0 && publicShown === shownRows.length}
                    disabled={!canEdit || savingVis || editableShown.length === 0}
                    onChange={() => {
                      const makePublic = publicShown < shownRows.length;
                      const n = editableShown.filter(r => isPublic(r) !== makePublic).length;
                      if (n > 1 && !window.confirm(`Mark ${n} guests ${makePublic ? 'Public' : 'Private'}${adder ? ` (added by ${adder.label})` : ''}?`)) return;
                      setVisibilityFor(editableShown, makePublic);
                    }} />
                </th>
                <th className="fn-group type-group" colSpan={GUEST_TYPE_OPTIONS.length} title="Tick a box to change the guest's type - saved at once">Guest Type</th>
                <th rowSpan={2}></th>
              </tr>
              <tr className="fn-head-row">
                {tableFunctions.map(f => (
                  <th key={f.FunctionId} className={'fn-col' + (isPastFunction(f) ? ' past' : '')}
                    title={`${f.FunctionName} · ${fmtDate(f.FunctionDate)}${f.StartTime || f.FunctionTime ? ` · ${fmtTime(f.StartTime || f.FunctionTime)}` : ''}${isPastFunction(f) ? ' (over)' : ''}`}>
                    <span className="fn-col-name">{f.FunctionName} <span className="fn-count">({fnCount(f.FunctionId)})</span></span>
                    <span className="fn-col-date">{fmtDate(f.FunctionDate).slice(0, 5)}{f.StartTime ? ` ${f.StartTime}` : ''}</span>
                  </th>
                ))}
                {GUEST_TYPE_OPTIONS.map(o => (
                  <th key={o.value} className={'fn-col type-col type-' + o.value.toLowerCase()}>
                    <span className="fn-col-name">{o.label}</span>
                    <span className="fn-col-date">{typeCounts[o.value === 'Family' ? 'relative' : o.value.toLowerCase()] || 0}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {listView === 'families'
                ? familyGroups.flatMap(g => {
                  const open = expanded.has(g.key);
                  const out = [renderRow(g.head, { lead: true, count: g.members.length, open, key: g.key })];
                  if (open) {
                    g.members.forEach(m => out.push(renderRow(m, { nested: true })));
                    // Add the next relative without leaving the list.
                    if (canAdd && mine(g.head)) {
                      out.push(
                        <FamilyAddRow key={`${g.key}-add`} head={g.head} span={3 + GUEST_TYPE_OPTIONS.length + tableFunctions.length}
                          onAdd={(member) => addOrMoveFamilyMember(g.head, member)} />
                      );
                    }
                  }
                  return out;
                })
                : (listView === 'individuals' ? individualRows
                  : listView === 'business' ? businessRows : filtered).map(row => renderRow(row))}
              {listView !== 'people' && viewCounts[listView] === 0 && (
                <tr><td colSpan={tableFunctions.length + 6 + GUEST_TYPE_OPTIONS.length} className="muted" style={{ padding: 16, textAlign: 'center' }}>
                  {listView === 'families' ? 'No families in this list.'
                    : listView === 'business' ? 'No business associates in this list — mark a guest "Business associate" in their form.'
                      : 'No individual guests in this list — everyone is in a family.'}
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {menu && (() => {
        const r = menu.row;
        const editable = mine(r);
        const fam = Number(r.FamilyId || 0);
        const label = fam && fam === Number(r.GuestId) ? 'Add family members' : 'Add family members (make head)';
        return (
          <div className="ctx-menu" style={{ left: menu.x, top: menu.y }} onClick={(e) => e.stopPropagation()} onContextMenu={(e) => e.preventDefault()}>
            <div className="ctx-title">{fullName(r)}</div>
            <button type="button" disabled={!(editable && canAdd)} onClick={() => { setMenu(null); openFamilyFor(r); }}>👪 {label}</button>
            <button type="button" disabled={!(editable && canEdit)} onClick={() => { setMenu(null); openEdit(r); }}>✎ Edit guest</button>
            <button type="button" disabled={!r.MobileNumber} onClick={() => { setMenu(null); openInvite(r); }}>💬 Invite on WhatsApp</button>
            <button type="button" className="danger" disabled={!(editable && canDelete)} onClick={() => { setMenu(null); del(r); }}>🗑 Delete</button>
          </div>
        );
      })()}

      {open && <MemberForm vm={vm} />}
      {inviteRow && (
        <Modal title="Invite on WhatsApp" onClose={closeInvite} footer={<button className="btn light" onClick={closeInvite} disabled={!!inviteSharing}>Cancel</button>}>
          <div style={{ display: 'grid', gap: 10 }}>
            {inviteCooldown > 0 && (
              <div className="invite-cooldown-note">
                Please wait {inviteCooldown} seconds before sending another invitation.
              </div>
            )}
            <button className="btn primary" disabled={!!inviteSharing || inviteCooldown > 0} onClick={() => sendInvite('image')}>
              {inviteSharing === 'image' ? 'Opening...' : 'Image Invite'}
            </button>
            <button className="btn light" disabled={!!inviteSharing || inviteCooldown > 0} onClick={() => sendInvite('text')}>
              {inviteSharing === 'text' ? 'Opening...' : 'Text Invite'}
            </button>
            <button className="btn light" disabled={!!inviteSharing || inviteCooldown > 0 || !guestHasAllFunctions(inviteRow)} onClick={() => sendInvite('pdf')}>
              {inviteSharing === 'pdf' ? 'Opening...' : 'PDF Invite'}
            </button>
            {!guestHasAllFunctions(inviteRow) && (
              <div className="muted" style={{ fontSize: 12 }}>PDF invite is available only when this guest is invited to all functions.</div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
